import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const RouterLink = forwardRef(({ to, target, ...props }, ref) => (
    <Link ref={ref} {...props} to={to} target={target} />
));
RouterLink.displayName = 'RouterLink';
RouterLink.propTypes = {
    to: PropTypes.string.isRequired,
    target: PropTypes.string,
};

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const location = useLocation();

    const Icon = item.icon;
    const isIconOpen = customization.isOpen.includes(item?.id);

    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size="1.3rem" />
    ) : (
        <FiberManualRecordIcon
            sx={{ width: isIconOpen ? 8 : 6, height: isIconOpen ? 8 : 6 }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const itemTarget = item.target ? '_blank' : '_self';

    const listItemProps = item?.external
        ? { component: 'a', href: item.url, target: itemTarget }
        : { component: RouterLink, to: item.url, target: itemTarget };

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    };

    // active menu item on page load and route change
    useEffect(() => {
        if (item.url && location.pathname.startsWith(item.url)) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
    }, [location.pathname]);

    const isSelected = customization.isOpen.includes(item.id);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: `${customization.borderRadius}px`,
                mb: 0.5,
                alignItems: 'flex-start',
                backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                py: level > 1 ? 1 : 1.25,
                pl: `${level * 24}px`,
                borderLeft: isSelected
                    ? `4px solid ${theme.palette.secondary.dark}`
                    : '4px solid transparent',
                transition: 'border-left 0.2s ease',
            }}
            selected={isSelected}
            onClick={() => itemHandler(item.id)}
        >
            <ListItemIcon sx={{ my: 'auto', minWidth: item?.icon ? 36 : 18 }}>{itemIcon}</ListItemIcon>
            <ListItemText
                primary={
                    <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit">
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
